
import BaseModalForm from "@/components/shared/modal/BaseModalForm.vue";
import { computed, defineComponent, onMounted, PropType, ref } from "@vue/composition-api";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { ModalType } from "@/shared/enums/modalTypeEnum";
import { openNotification } from "@/shared/helpers/store.helpers";
import { setShowLoadingSpinner } from "@/shared/helpers/store.helpers";
import {
  validateNotEmpty,
  validateIsNorwegianPhoneNumber,
  validateIsOrganizationNumber,
  validateIsEmail,
  validateIsNorwegianZipCode,
} from "@/shared/helpers/validationHelpers";
import { ApiGetCustomerDto } from "@/api/generated/Api";
import { useStore } from "@/shared/useHelpers";
import { StoreState } from "@/store/store.state.interface";
import Vue from "vue/types/umd";
import { api } from "@/api/api";
import { formatRelative } from "@/shared/helpers/dateHelpers";

export default defineComponent({
  name: "RegisterOrganizationModal",
  components: { BaseModalForm },
  props: {
    type: {
      type: String as PropType<ModalType>,
      required: true,
    },
    headline: {
      type: String,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
  },
  emits: ["close", "refetch"],
  setup(props, context) {
    const store = useStore<StoreState>();
    const currentOrganization = ref<ApiGetCustomerDto>(getInitialOrganizationValues());
    const isLoading = ref(false);

    if (props.type === ModalType.Add) {
      currentOrganization.value.isActive = true;
    }

    const countryItems = [{ label: "Norge", value: "NO" }];

    const validForm = ref(false);
    const sameAsInvoiceAddress = ref(true);

    const setInvoiceAddressSameAsAddress = async () => {
      currentOrganization.value.invoiceAddress1 = currentOrganization.value.postAddress1;
      currentOrganization.value.invoiceZip = currentOrganization.value.postZip;
      currentOrganization.value.invoiceCity = currentOrganization.value.postCity;
      currentOrganization.value.invoiceCountry = currentOrganization.value.postCountry;
    };

    const getOrganizationDetails = async () => {
      isLoading.value = true;
      currentOrganization.value = (await api.customer.getCustomerOrgByIdAsync(props.organizationId)).data;
      isLoading.value = false;
    };

    const handleSubmit = async () => {
      const isValid = (context.refs.form as Vue & { validate: () => boolean }).validate();
      if (!isValid) {
        return;
      }
      setShowLoadingSpinner(store, true);
      try {
        if (sameAsInvoiceAddress.value) {
          setInvoiceAddressSameAsAddress();
        }

        if (props.type === ModalType.Add) {
          await api.customer.createCustomerOrganizationAsync(currentOrganization.value);
          context.emit("close");
          context.emit("refetch");
        } else if (props.type === ModalType.Edit) {
          await api.customer.updateOrganizationAsync(currentOrganization.value.id, currentOrganization.value);
          context.emit("close");
          context.emit("refetch");
        }
      } catch (err) {
        openNotification(store, NotificationItemType.Error, "En feil oppsto ved lagring.");
      } finally {
        setShowLoadingSpinner(store, false);
      }
    };
    onMounted(async () => {
      if (props.type !== ModalType.Add) {
        await getOrganizationDetails();

        if (currentOrganization.value.invoiceAddress1) {
          sameAsInvoiceAddress.value = false;
        }
      }
    });
    return {
      countryItems,
      validForm,
      sameAsInvoiceAddress,
      readonly: computed(() => props.type === ModalType.Display),
      currentOrganization,
      handleSubmit,
      validateNotEmpty,
      validateIsNorwegianPhoneNumber,
      validateIsOrganizationNumber,
      validateIsEmail,
      validateIsNorwegianZipCode,
      formatRelative,
      ModalType,
      isLoading,
    };
  },
});

const getInitialOrganizationValues = (): ApiGetCustomerDto => ({
  id: 0,
  isActive: false,
  insertDate: "",
  insertedBy: 0,
  updateDate: "",
  updatedBy: 0,
  lastVerifiedContactInfoDate: "",
  invoiceAddress1: "",
  invoiceZip: "",
  invoiceCity: "",
  invoiceCountry: "",
  customer: {
    customerId: 0,
    name: "",
    orgNr: "",
    homePage: "",
  },
  adUserId: "",
});
